import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { isViewer, activeWorkspace } from "@/lib/guards";
import { friendlyError } from "@/lib/errors";
import { SWITCHER } from "@/constants/testIds/profile";
import { Eye, Loader2, ShieldAlert } from "lucide-react";
import { ADMIN } from "@/constants/testIds/admin";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Thin global banner shown while the user browses a shared workspace read-only
// (Epic 10.3). It sits in normal flow at the very top of the app shell so page
// sticky headers keep working; tapping it returns to the user's own account.
export default function ViewerBanner() {
  const { user, switchWorkspace, impersonation, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const onExpiry = () => navigate("/admin", { replace: true });
    window.addEventListener("valiosia:impersonation-expired", onExpiry);
    return () => window.removeEventListener("valiosia:impersonation-expired", onExpiry);
  }, [navigate]);

  const impersonating = Boolean(impersonation);
  const expires = impersonation?.expiresAt ? new Date(impersonation.expiresAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "em breve";

  if (impersonating) {
    const returnToAdmin = async () => {
      if (leaving) return;
      setLeaving(true);
      try { await stopImpersonation(); navigate("/admin", { replace: true }); toast.success("De volta à sua conta de admin ✦"); }
      catch (err) { toast.error(friendlyError(err)); }
      finally { setLeaving(false); }
    };
    return (
      <div data-testid={ADMIN.impersonationBanner} className="sticky top-0 z-[60] w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-rose text-white text-[12px] font-ui">
        {leaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />}
        <span className="truncate">Você está atendendo <strong>{user?.name || user?.email}</strong> · até {expires}</span>
        <button type="button" data-testid={ADMIN.impersonationReturn} onClick={returnToAdmin} disabled={leaving} className="underline underline-offset-2 shrink-0">voltar ao admin</button>
      </div>
    );
  }

  if (!isViewer(user)) return null;

  const ws = activeWorkspace(user);
  const marca = ws?.marca || "esta marca";

  const exit = async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      await switchWorkspace(null);
      toast.success("De volta à sua conta ✦");
    } catch (err) {
      toast.error(friendlyError(err));
      setLeaving(false);
    }
  };

  return (
    <button
      type="button"
      data-testid={SWITCHER.banner}
      onClick={exit}
      disabled={leaving}
      aria-label={`Vendo ${marca} como colaborador, somente leitura. Toque para voltar à sua conta.`}
      className="w-full flex items-center justify-center gap-2 px-4 py-1.5 bg-brand-rose/12 border-b border-brand-rose/30 text-brand-rose text-[12px] font-ui hover:bg-brand-rose/20 transition-colors"
    >
      {leaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
      <span className="truncate">
        Vendo <span className="font-semibold">{marca}</span> como colaborador · somente leitura
      </span>
      <span data-testid={SWITCHER.bannerExit} className="underline underline-offset-2 shrink-0">voltar</span>
    </button>
  );
}
